import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { MAX_DATA_WINDOW_DAYS } from '../filterParameterMapping';
import { getAlarmRuleDataWindowError, getAlarmRuleDataWindowSeconds } from '../alarm-rule/alarm-rule-validation';

const DateFilter = ({
  startDate,
  endDate,
  mode = 'date',
  value = null,
  units = [
    { value: 'date', label: 'Date' },
    { value: 'day', label: 'Day' },
    { value: 'hours', label: 'Hours' },
  ],
  defaultWindowHours = 72,
  onStartDateChange,
  onEndDateChange,
  onModeChange,
  onValueChange,
  onReset,
  showDateReset = true,
  dateRelativeLabel = '',
  dateWindowErrorId,
  dataWindowSeconds,
  validateAlarmWindow = false,
  onValidityChange,
  onInitializeRange,
}) => {
  // Populate an initial visible range (matching the legacy TimePicker default)
  // instead of showing empty inputs until the user clicks Reset dates.
  const hasInitializedRange = useRef(false);
  const dataWindowChangeTimer = useRef(null);
  // Single source of truth for the typed text; timers/handlers read this instead of
  // React state so they never act on a stale closure value.
  const latestDataWindowInput = useRef(value ?? '');
  const lastCommittedDataWindow = useRef(value ?? '');
  const [dataWindowInput, setDataWindowInput] = useState(value ?? '');
  const [touched, setTouched] = useState(false);
  const windowError = validateAlarmWindow
    ? getAlarmRuleDataWindowError(getAlarmRuleDataWindowSeconds({
      dataWindowMode: mode,
      dataWindowValue: dataWindowInput,
      dataWindowSeconds: String(dataWindowInput) === String(value ?? '') ? dataWindowSeconds : null,
      startDate,
      endDate,
    })) : '';
  const validationError = touched || (mode === 'date' ? startDate && endDate : dataWindowInput !== '') ? windowError : '';
  const errorMessage = validationError || (dateWindowErrorId ? `Maximum ${MAX_DATA_WINDOW_DAYS} days.` : '');
  const errorId = errorMessage ? dateWindowErrorId || 'analysis-filters-panel-date-window-error' : undefined;
  useEffect(() => {
    if (onValidityChange) onValidityChange(!windowError);
  }, [windowError, onValidityChange]);

  const commitDataWindowValue = inputValue => {
    const nextValue = inputValue === '' ? null : Number(inputValue);
    if ((!validateAlarmWindow && !(nextValue > 0)) || Object.is(nextValue, lastCommittedDataWindow.current)) return;

    lastCommittedDataWindow.current = nextValue;
    onValueChange(nextValue);
  };

  const handleDataWindowInputChange = event => {
    const nextValue = event.target.value;
    latestDataWindowInput.current = nextValue;
    setDataWindowInput(nextValue);
    setTouched(true);
    clearTimeout(dataWindowChangeTimer.current);
    if (validateAlarmWindow) commitDataWindowValue(nextValue);
    else dataWindowChangeTimer.current = setTimeout(() => commitDataWindowValue(latestDataWindowInput.current), 300);
  };

  const handleDataWindowInputBlur = () => {
    setTouched(true);
    clearTimeout(dataWindowChangeTimer.current);
    commitDataWindowValue(latestDataWindowInput.current);
  };

  useEffect(() => {
    const incoming = value ?? '';
    // Skip echoes of the value we just committed so an in-flight keystroke isn't overwritten.
    if (String(incoming) === String(lastCommittedDataWindow.current ?? '')) return;

    lastCommittedDataWindow.current = incoming;
    latestDataWindowInput.current = incoming;
    setDataWindowInput(incoming);
  }, [mode, value]);

  useEffect(() => () => clearTimeout(dataWindowChangeTimer.current), []);

  useEffect(() => {
    if (hasInitializedRange.current || mode !== 'date' || startDate || endDate) return;
    hasInitializedRange.current = true;

    const end = new Date();
    end.setSeconds(0, 0);
    const start = new Date(end.getTime() - defaultWindowHours * 60 * 60 * 1000);

    if (onInitializeRange) {
      onInitializeRange(start, end);
    } else {
      onStartDateChange(start);
      onEndDateChange(end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
  <div className={`analysis-create-rule-container__window analysis-filters-panel__dates validation-callout-anchor${errorId && mode !== 'date' ? ' analysis-filters-panel__dates--invalid' : ''}`} data-testid="analysis-filters-panel-date-filters">
    {errorId && (
      <p id={errorId} className="validation-callout alarm-rule-form__data-window-error" role="alert" data-testid={errorId}>
        {errorMessage}
      </p>
    )}
    {mode === 'date' ? (
      <div className="analysis-filters-panel__date-pickers">
        <DatePicker
          popperPlacement="bottom-start"
          selected={startDate || null}
          selectsStart
          startDate={startDate || null}
          endDate={endDate || null}
          maxDate={new Date()}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="dd/MM/yyyy HH:mm"
          showIcon
          toggleCalendarOnIconClick
          onChange={date => { setTouched(true); onStartDateChange(date); }}
          customInput={<input className="form-control" aria-invalid={!!errorId} aria-describedby={errorId} data-testid="analysis-filters-panel-start-date" />}
        />
        <DatePicker
          popperPlacement="bottom-start"
          selected={endDate || null}
          selectsEnd
          startDate={startDate || null}
          endDate={endDate || null}
          minDate={startDate || null}
          maxDate={new Date()}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="dd/MM/yyyy HH:mm"
          showIcon
          toggleCalendarOnIconClick
          onChange={date => { setTouched(true); onEndDateChange(date); }}
          customInput={<input className="form-control" aria-invalid={!!errorId} aria-describedby={errorId} data-testid="analysis-filters-panel-end-date" />}
        />
      </div>
    ) : (
      <>
        <span className="analysis-create-rule-container__window-label">{dateRelativeLabel}</span>
        <input
          type="number"
          id="analysis-filters-panel-date-window-value"
          name="analysis-filters-panel-date-window-value"
          className={`form-control analysis-create-rule-container__input analysis-create-rule-container__input--window${errorId ? ' analysis-create-rule-container__input--error' : ''}`}
          value={dataWindowInput}
          aria-invalid={!!errorId}
          aria-describedby={errorId}
          min={validateAlarmWindow ? '0' : '1'}
          step={validateAlarmWindow ? 'any' : undefined}
          placeholder="3"
          onChange={handleDataWindowInputChange}
          onBlur={handleDataWindowInputBlur}
          data-testid="analysis-filters-panel-date-window-value"
        />
      </>
    )}
    <div className="select-with-chevron">
      <select
        id="analysis-filters-panel-date-window-mode"
        name="analysis-filters-panel-date-window-mode"
        className="form-control analysis-create-rule-container__select"
        value={mode}
        onChange={event => onModeChange(event.target.value)}
        data-testid="analysis-filters-panel-date-window-mode">
        {units.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
      </select>
      <i className="fas fa-chevron-down" aria-hidden="true" />
    </div>
    {showDateReset && (
      <button type="button" className="btn btn-secondary reset-dates-button" onClick={onReset} data-testid="analysis-filters-panel-date-window-reset">
        <i className="fas fa-undo fa-fw" aria-hidden="true" /> Reset dates
      </button>
    )}
  </div>
  );
};

DateFilter.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  mode: PropTypes.string,
  value: PropTypes.number,
  units: PropTypes.array,
  defaultWindowHours: PropTypes.number,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  showDateReset: PropTypes.bool,
  dateRelativeLabel: PropTypes.string,
  dateWindowErrorId: PropTypes.string,
  dataWindowSeconds: PropTypes.number,
  validateAlarmWindow: PropTypes.bool,
  onValidityChange: PropTypes.func,
  onInitializeRange: PropTypes.func,
};

export default DateFilter;